/**
 * Topic Interface Definitions for Insights Hub
 *
 * Comprehensive TypeScript interfaces for topic management including:
 * - Topic categorization and organization
 * - Metadata for topic display and filtering
 * - Topic statistics and analytics
 * - Topic hierarchies and relationships
 *
 * Compatible with Vue.js 2 and backend API specifications
 */

import { QuestionCategory, QuestionPriority } from './Question';

// =============================================================================
// INTERFACES FOR QUESTION SUGGESTIONS
// =============================================================================

/**
 * Question suggestion interface
 */
export interface IQuestionSuggestion {
  id: string;
  question_text: string;
  instruction: string;
  category: QuestionCategory;
  priority: QuestionPriority;
  confidence_score: number;
  metadata?: {
    suggested_by?: string;
    complexity_level?: 'beginner' | 'intermediate' | 'advanced';
    estimated_execution_time?: number;
    tags?: string[];
  };
}

// =============================================================================
// ENUMS AND CONSTANTS
// =============================================================================

/**
 * Topic visibility levels
 */
export enum TopicVisibility {
  PUBLIC = 'public',
  PRIVATE = 'private',
  SHARED = 'shared',
  ARCHIVED = 'archived',
}

/**
 * Topic types for different use cases
 */
export enum TopicType {
  BUSINESS_METRICS = 'business_metrics',
  OPERATIONAL_INSIGHTS = 'operational_insights',
  STRATEGIC_ANALYSIS = 'strategic_analysis',
  PERFORMANCE_MONITORING = 'performance_monitoring',
  TREND_ANALYSIS = 'trend_analysis',
  COMPARATIVE_ANALYSIS = 'comparative_analysis',
  FORECASTING = 'forecasting',
  CUSTOM = 'custom',
}

/**
 * Topic status for lifecycle management
 */
export enum TopicStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  DRAFT = 'draft',
  ARCHIVED = 'archived',
  DEPRECATED = 'deprecated',
}

/**
 * Data freshness indicators
 */
export enum DataFreshness {
  REAL_TIME = 'real_time',
  HOURLY = 'hourly',
  DAILY = 'daily',
  WEEKLY = 'weekly',
  MONTHLY = 'monthly',
  STALE = 'stale',
}

// =============================================================================
// CORE TOPIC INTERFACES
// =============================================================================

/**
 * Base topic interface with core properties
 */
export interface IBaseTopic {
  /** Unique topic identifier */
  id: string;

  /** Topic name/title */
  name: string;

  /** Detailed topic description */
  description?: string;

  /** Topic category alignment */
  category: QuestionCategory | string;

  /** Topic type classification */
  type: TopicType;

  /** Topic visibility level */
  visibility: TopicVisibility;

  /** Topic status */
  status: TopicStatus;

  /** Priority level */
  priority: QuestionPriority;

  /** Topic tags for filtering */
  tags: string[];

  /** When the topic was created */
  created_at: string;

  /** When the topic was last updated */
  updated_at: string;

  /** Topic creator information */
  created_by?: ITopicCreator;

  /** Topic metadata */
  metadata?: ITopicMetadata;
}

/**
 * Topic creator information
 */
export interface ITopicCreator {
  /** Creator user ID */
  user_id: number;

  /** Creator name */
  name: string;

  /** Creator role */
  role?: string;

  /** Creator email */
  email?: string;
}

/**
 * Topic metadata for enhanced functionality
 */
export interface ITopicMetadata {
  /** Business domain this topic relates to */
  business_domain?: string;

  /** Estimated complexity (0-1 scale) */
  complexity_score?: number;

  /** Expected data volume */
  data_volume_estimate?: 'small' | 'medium' | 'large' | 'very_large';

  /** Data freshness requirement */
  data_freshness: DataFreshness;

  /** Refresh frequency in hours */
  refresh_frequency_hours?: number;

  /** Auto-refresh enabled */
  auto_refresh_enabled?: boolean;

  /** Topic icon for UI */
  icon?: string;

  /** Color code for UI theming */
  color?: string;

  /** Display order for UI */
  display_order?: number;

  /** Related business KPIs */
  related_kpis?: string[];

  /** Data dependencies */
  data_dependencies?: string[];

  /** Custom metadata fields */
  [key: string]: any;
}

// =============================================================================
// TOPIC HIERARCHY AND RELATIONSHIPS
// =============================================================================

/**
 * Topic hierarchy interface for nested topic structures
 */
export interface ITopicHierarchy extends IBaseTopic {
  /** Parent topic ID if this is a subtopic */
  parent_id?: string;

  /** Child topics */
  children?: ITopicHierarchy[];

  /** Hierarchy level (0 = root) */
  level: number;

  /** Full path from root */
  path: string[];

  /** Whether this topic has children */
  has_children: boolean;
}

/**
 * Topic relationship types
 */
export enum TopicRelationType {
  PARENT_CHILD = 'parent_child',
  RELATED = 'related',
  DEPENDENT = 'dependent',
  PREREQUISITE = 'prerequisite',
  ALTERNATIVE = 'alternative',
}

/**
 * Topic relationship interface
 */
export interface ITopicRelationship {
  /** Relationship ID */
  id: string;

  /** Source topic ID */
  source_topic_id: string;

  /** Target topic ID */
  target_topic_id: string;

  /** Relationship type */
  type: TopicRelationType;

  /** Relationship strength (0-1 scale) */
  strength?: number;

  /** Relationship description */
  description?: string;

  /** When relationship was created */
  created_at: string;
}

// =============================================================================
// TOPIC ANALYTICS AND STATISTICS
// =============================================================================

/**
 * Topic analytics data
 */
export interface ITopicAnalytics {
  /** Number of questions in this topic */
  question_count: number;

  /** Total executions across all questions */
  total_executions: number;

  /** Average execution time */
  avg_execution_time: number;

  /** Success rate (0-1) */
  success_rate: number;

  /** Last activity timestamp */
  last_activity_at?: string;

  /** Most popular questions in this topic */
  popular_questions: Array<{
    question_text: string;
    execution_count: number;
  }>;

  /** Execution trends over time */
  execution_trends: ITopicExecutionTrend[];

  /** User engagement metrics */
  engagement_metrics: ITopicEngagementMetrics;

  /** Data quality metrics */
  data_quality: ITopicDataQuality;
}

/**
 * Topic execution trend data
 */
export interface ITopicExecutionTrend {
  /** Time period */
  period: string;

  /** Number of executions */
  execution_count: number;

  /** Number of unique users */
  unique_users: number;

  /** Average response time */
  avg_response_time: number;

  /** Success rate for this period */
  success_rate: number;
}

/**
 * Topic engagement metrics
 */
export interface ITopicEngagementMetrics {
  /** Number of unique users */
  unique_users: number;

  /** Average sessions per user */
  avg_sessions_per_user: number;

  /** Average questions per session */
  avg_questions_per_session: number;

  /** User retention rate */
  retention_rate: number;

  /** Time spent on topic (seconds) */
  avg_time_spent: number;

  /** User satisfaction score (0-5) */
  satisfaction_score?: number;
}

/**
 * Topic data quality metrics
 */
export interface ITopicDataQuality {
  /** Data completeness score (0-1) */
  completeness_score: number;

  /** Data accuracy score (0-1) */
  accuracy_score: number;

  /** Data freshness indicator */
  freshness: DataFreshness;

  /** Last data update timestamp */
  last_data_update: string;

  /** Data validation status */
  validation_status: 'valid' | 'warning' | 'error';

  /** Data quality issues if any */
  quality_issues?: string[];
}

// =============================================================================
// TOPIC DISCOVERY AND RECOMMENDATIONS
// =============================================================================

/**
 * Topic discovery interface for finding relevant topics
 */
export interface ITopicDiscovery {
  /** Discovered topic */
  topic: IBaseTopic;

  /** Relevance score (0-1) for user context */
  relevance_score: number;

  /** Why this topic is relevant */
  relevance_reason: string;

  /** Suggested questions for this topic */
  suggested_questions: IQuestionSuggestion[];

  /** Related data insights */
  data_insights?: string[];

  /** Estimated value/impact */
  estimated_impact?: 'low' | 'medium' | 'high';
}

/**
 * Topic recommendation request
 */
export interface ITopicRecommendationRequest {
  /** User context for recommendations */
  user_context?: {
    /** User role */
    role?: string;

    /** Recently viewed topics */
    recent_topics?: string[];

    /** User preferences */
    preferences?: string[];

    /** Current business focus */
    business_focus?: string;
  };

  /** Limit number of recommendations */
  limit?: number;

  /** Include analytics in recommendations */
  include_analytics?: boolean;
}

// =============================================================================
// TOPIC MANAGEMENT INTERFACES
// =============================================================================

/**
 * Request interface for creating topics
 */
export interface ICreateTopicRequest {
  /** Topic name */
  name: string;

  /** Topic description */
  description?: string;

  /** Topic category */
  category: QuestionCategory | string;

  /** Topic type */
  type: TopicType;

  /** Visibility level */
  visibility: TopicVisibility;

  /** Priority level */
  priority: QuestionPriority;

  /** Topic tags */
  tags: string[];

  /** Parent topic ID for hierarchical topics */
  parent_id?: string;

  /** Topic metadata */
  metadata?: Partial<ITopicMetadata>;
}

/**
 * Request interface for updating topics
 */
export interface IUpdateTopicRequest {
  /** Updated name */
  name?: string;

  /** Updated description */
  description?: string;

  /** Updated category */
  category?: QuestionCategory | string;

  /** Updated type */
  type?: TopicType;

  /** Updated visibility */
  visibility?: TopicVisibility;

  /** Updated status */
  status?: TopicStatus;

  /** Updated priority */
  priority?: QuestionPriority;

  /** Updated tags */
  tags?: string[];

  /** Updated metadata */
  metadata?: Partial<ITopicMetadata>;
}

/**
 * Request interface for fetching topics with filtering
 */
export interface IFetchTopicsRequest {
  /** Filter by category */
  category?: QuestionCategory | string;

  /** Filter by type */
  type?: TopicType;

  /** Filter by visibility */
  visibility?: TopicVisibility;

  /** Filter by status */
  status?: TopicStatus;

  /** Filter by priority */
  priority?: QuestionPriority;

  /** Search in topic name/description */
  search?: string;

  /** Filter by tags */
  tags?: string[];

  /** Include only topics with questions */
  has_questions?: boolean;

  /** Include analytics data */
  include_analytics?: boolean;

  /** Include hierarchy information */
  include_hierarchy?: boolean;

  /** Sort field */
  sort_by?: 'name' | 'created_at' | 'updated_at' | 'priority' | 'question_count' | 'popularity';

  /** Sort order */
  sort_order?: 'asc' | 'desc';

  /** Results per page */
  limit?: number;

  /** Results offset */
  offset?: number;
}

// =============================================================================
// TOPIC CONFIGURATION AND SETTINGS
// =============================================================================

/**
 * Topic configuration interface for system settings
 */
export interface ITopicConfiguration {
  /** Auto-discovery enabled */
  auto_discovery_enabled: boolean;

  /** Default data freshness requirement */
  default_data_freshness: DataFreshness;

  /** Default refresh frequency in hours */
  default_refresh_frequency: number;

  /** Maximum topics per user/merchant */
  max_topics_per_user: number;

  /** Topic retention policy in days */
  retention_policy_days: number;

  /** Enable topic recommendations */
  recommendations_enabled: boolean;

  /** Analytics retention period in days */
  analytics_retention_days: number;
}

/**
 * Topic permissions interface
 */
export interface ITopicPermissions {
  /** Can create topics */
  can_create: boolean;

  /** Can edit topics */
  can_edit: boolean;

  /** Can delete topics */
  can_delete: boolean;

  /** Can view private topics */
  can_view_private: boolean;

  /** Can manage topic hierarchy */
  can_manage_hierarchy: boolean;

  /** Can access analytics */
  can_access_analytics: boolean;
}

// =============================================================================
// TOPIC INSIGHTS AND INTELLIGENCE
// =============================================================================

/**
 * Topic intelligence interface for AI-powered insights
 */
export interface ITopicIntelligence {
  /** Topic ID */
  topic_id: string;

  /** AI-generated topic summary */
  summary: string;

  /** Key insights about this topic */
  key_insights: string[];

  /** Recommended actions */
  recommended_actions: Array<{
    action: string;
    priority: QuestionPriority;
    estimated_impact: string;
  }>;

  /** Data patterns detected */
  patterns: Array<{
    pattern_type: string;
    description: string;
    confidence: number;
  }>;

  /** Trending aspects */
  trending_aspects: string[];

  /** Performance indicators */
  performance_indicators: Array<{
    name: string;
    value: number;
    trend: 'up' | 'down' | 'stable';
    benchmark?: number;
  }>;

  /** Generated at timestamp */
  generated_at: string;

  /** Confidence score for insights */
  confidence_score: number;
}

// =============================================================================
// DISPLAY AND UI HELPERS
// =============================================================================

/**
 * Display helpers for topic UI components
 */
export class TopicDisplayHelpers {
  /**
   * Get human-readable topic type name
   */
  public static getTopicTypeDisplayName(type: TopicType): string {
    const typeMap: Record<TopicType, string> = {
      [TopicType.BUSINESS_METRICS]: 'Business Metrics',
      [TopicType.OPERATIONAL_INSIGHTS]: 'Operational Insights',
      [TopicType.STRATEGIC_ANALYSIS]: 'Strategic Analysis',
      [TopicType.PERFORMANCE_MONITORING]: 'Performance Monitoring',
      [TopicType.TREND_ANALYSIS]: 'Trend Analysis',
      [TopicType.COMPARATIVE_ANALYSIS]: 'Comparative Analysis',
      [TopicType.FORECASTING]: 'Forecasting',
      [TopicType.CUSTOM]: 'Custom',
    };

    return typeMap[type] || type;
  }

  /**
   * Get visibility icon for UI
   */
  public static getVisibilityIcon(visibility: TopicVisibility): string {
    const iconMap: Record<TopicVisibility, string> = {
      [TopicVisibility.PUBLIC]: 'mdi-earth',
      [TopicVisibility.PRIVATE]: 'mdi-lock',
      [TopicVisibility.SHARED]: 'mdi-account-group',
      [TopicVisibility.ARCHIVED]: 'mdi-archive',
    };

    return iconMap[visibility] || 'mdi-help-circle';
  }

  /**
   * Get status color for UI styling
   */
  public static getStatusColor(status: TopicStatus): string {
    const colorMap: Record<TopicStatus, string> = {
      [TopicStatus.ACTIVE]: 'success',
      [TopicStatus.INACTIVE]: 'grey',
      [TopicStatus.DRAFT]: 'info',
      [TopicStatus.ARCHIVED]: 'grey',
      [TopicStatus.DEPRECATED]: 'warning',
    };

    return colorMap[status] || 'grey';
  }

  /**
   * Get data freshness color for UI
   */
  public static getDataFreshnessColor(freshness: DataFreshness): string {
    const colorMap: Record<DataFreshness, string> = {
      [DataFreshness.REAL_TIME]: 'success',
      [DataFreshness.HOURLY]: 'success',
      [DataFreshness.DAILY]: 'info',
      [DataFreshness.WEEKLY]: 'warning',
      [DataFreshness.MONTHLY]: 'warning',
      [DataFreshness.STALE]: 'error',
    };

    return colorMap[freshness] || 'grey';
  }

  /**
   * Format topic statistics for display
   */
  public static formatTopicStats(analytics: ITopicAnalytics): string {
    const { question_count, total_executions } = analytics;
    return `${question_count} questions • ${total_executions} executions`;
  }

  /**
   * Generate topic breadcrumb from hierarchy
   */
  public static generateBreadcrumb(topic: ITopicHierarchy): Array<{ name: string; id: string }> {
    return topic.path.map((id, index) => ({
      id,
      name: index === topic.path.length - 1 ? topic.name : `Level ${index + 1}`,
    }));
  }
}

// =============================================================================
// TOPIC VALIDATION
// =============================================================================

/**
 * Topic validation helper class
 */
export class TopicValidation {
  /**
   * Validate topic name
   */
  public static validateTopicName(name: string): boolean {
    return Boolean(name && name.trim().length >= 3 && name.trim().length <= 100);
  }

  /**
   * Validate topic type
   */
  public static validateTopicType(type: string): boolean {
    return Object.values(TopicType).includes(type as TopicType);
  }

  /**
   * Validate visibility level
   */
  public static validateVisibility(visibility: string): boolean {
    return Object.values(TopicVisibility).includes(visibility as TopicVisibility);
  }

  /**
   * Validate status
   */
  public static validateStatus(status: string): boolean {
    return Object.values(TopicStatus).includes(status as TopicStatus);
  }

  /**
   * Validate data freshness
   */
  public static validateDataFreshness(freshness: string): boolean {
    return Object.values(DataFreshness).includes(freshness as DataFreshness);
  }

  /**
   * Validate tag format
   */
  public static validateTags(tags: string[]): boolean {
    return tags.every((tag) =>
      Boolean(tag) &&
      tag.trim().length >= 2 &&
      tag.trim().length <= 50 &&
      /^[a-zA-Z0-9_-]+$/.test(tag.trim()),
    );
  }
}

// =============================================================================
// INTERFACES AND TYPES EXPORTED INDIVIDUALLY ABOVE
// =============================================================================
